import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/risk-management';

const RiskManagementLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Risk Management Command Center | Explanatum';
    const desc = 'Identify, score, and mitigate business risks with AI workflows—without tangled spreadsheets or expensive enterprise GRC retainers.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Risk management overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-rose-400/40 bg-rose-500/10 px-4 py-1 text-sm font-semibold text-rose-100">
                <span className="inline-flex h-2 w-2 rounded-full bg-rose-300" />
                AI Risk Management Command Center
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Spot threats, score exposure, and launch mitigation in hours.
                <span className="mt-2 block text-blue-600">
                  Replace fragmented updates with an always-on risk cockpit.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Build live risk registers, track mitigation status, and generate AI-driven action plans for executives. Explanatum eliminates spreadsheet chaos and high-fee consultants with real-time risk intelligence tied to your operations.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_risk_ops_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-rose-500 via-sky-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-rose-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/finance"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open risk workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Centralised risk register with scoring, filters, and audit history
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  AI mitigation strategies applied directly to action plans
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-rose-300" />
                  Executive dashboards highlighting critical exposure in minutes
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Risk dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-rose-500/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Risk pulse</span>
                  <span>Live summary</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Critical exposure down 18% month-on-month • 27 risks tracked • AI strategy updates synced to mitigation plans.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Filters applied: Regulatory & Supply Chain • Heatmap highlights two high-probability vendor issues.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Board brief drafted with mitigation owners, deadlines, and risk score trendlines.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Operationalise risk decisions across the business</h2>
            <p className="mt-4 text-base text-gray-500">Keep leaders updated, assign mitigation, and ensure accountability without chasing static decks.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Dynamic risk registers',
              body: 'Map operational, financial, market, and regulatory risks with scoring, ownership, and timelines in one system.'
            }, {
              title: 'AI mitigation workflows',
              body: 'Generate targeted mitigation strategies that slot directly into existing plans and update status automatically.'
            }, {
              title: 'Executive dashboards',
              body: 'Surface critical exposure, trends, and runway impact for board reviews without weekend spreadsheet work.'
            }, {
              title: 'Filter & audit history',
              body: 'Slice by severity, probability, or mitigation status while maintaining a full audit trail for compliance requests.'
            }, {
              title: 'Collaboration & alerts',
              body: 'Notify stakeholders when deadlines slip, ensure owners acknowledge tasks, and log commentary for auditors.'
            }, {
              title: 'Consultant-level insights',
              body: 'Pair with the AI Business Consultant to produce risk memos, scenario models, and investor-ready narratives on demand.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams move off spreadsheets and enterprise GRC retainers</h2>
            <p className="mt-4 text-base text-gray-500">Achieve continuous monitoring and reporting agility without babysitting brittle templates or paying consultants per update.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Risk Management Command Center',
              points: [
                'Unified risk register with scoring, filters, and ownership',
                'AI mitigation strategies applied directly to action plans',
                'Executive-ready dashboards and exports on demand',
                'Collaboration for finance, ops, and leadership in one workspace',
                'Predictable subscription with rapid deployment'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Manual spreadsheets & slide decks',
              points: [
                'Out-of-date risk scores and missing context across tabs',
                'No automated mitigation prompts or accountability tracking',
                'Slow to consolidate filters or scenario views for leadership',
                'Version control issues erode confidence in the data',
                'Significant time lost preparing board-ready updates'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Enterprise GRC suites & risk consultants',
              points: [
                'Multi-month implementations with six-figure retainers',
                'Insights delivered via quarterly slide decks, not live systems',
                'Limited flexibility when requirements change mid-quarter',
                'Specialist teams required to operate complex tooling',
                'Additional fees for each workshop or mitigation refresh'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-rose-400/30 bg-rose-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need board-ready risk visibility before your next review?”</h3>
            <p className="mt-3 text-base text-gray-600">Log risks, apply AI mitigation strategies, and publish executive dashboards today—no consultant kickoff required.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_risk_ops_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-rose-500 via-sky-500 to-indigo-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-rose-400/30 hover:scale-[1.02]"
              >
                Launch risk command center
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where proactive risk management unlocks confidence</h2>
            <p className="mt-4 text-base text-gray-500">Give finance, operations, and leadership one source of truth for every risk conversation.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Scale-ups preparing for audits',
              body: 'Maintain regulatory and operational risk evidence with full history before investors and auditors ask.'
            }, {
              title: 'Global supply chain teams',
              body: 'Monitor vendor disruption, compliance gaps, and mitigation owners across regions in real time.'
            }, {
              title: 'CFOs and finance leaders',
              body: 'Connect risk exposure to cash flow impact, runway, and board reporting without manual reconciliations.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default RiskManagementLanding;
